import { useState, useMemo } from 'react'
import { Box, Button, TextField, Theme, Typography, useMediaQuery } from '@mui/material'
import DataTable from 'react-data-table-component'
import { getNestedValue } from '../../utils/getNestedValue'

const ListTable = ({
  data,
  handleClickRow,
  ids,
  fullData,
  path,
  singleLevelSSCC,
}: {
  data: any
  handleClickRow: (row: any) => void
  ids: string[]
  fullData: any
  path: string
  singleLevelSSCC: boolean
}) => {
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [inFocus, setFocus] = useState(false)
  // Filtering logic
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return []

    return data.filter((item: any) => {
      if (searchText === '') return true

      if (typeof item === 'string') {
        return item.toLowerCase().includes(searchText.toLowerCase())
      }

      if (typeof item === 'object') {
        return item.id.toLowerCase().includes(searchText.toLowerCase())
      }

      return false
    })
  }, [data, searchText])

  // Pagination logic
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage
    return filteredData.slice(startIndex, startIndex + rowsPerPage)
  }, [filteredData, currentPage, rowsPerPage])
  function checkForChildren(rowId: string) {
    if (path) {
      const pathArray = path.split(',')
      const currentList = getNestedValue(fullData, [...pathArray, rowId])

      return Boolean(currentList?.subChild?.length)
    }

    if (fullData?.[0]?.parsedJsonDto) {
      const parsedJsonDto = fullData[0].parsedJsonDto.find(
        (item: { sgtin: string }) => item.sgtin === rowId,
      )

      return Boolean(parsedJsonDto?.subChild?.length)
    }

    return false
  }

  const columnsForReportCardDetails = [
    {
      name: (
        <Typography fontSize={14} sx={{ fontFamily: 'Atlas-Grotesk-Bold' }}>
          {ids?.length === 1 && !singleLevelSSCC ? 'Parent Serials' : 'Serials'}
        </Typography>
      ),
      selector: (row: any) => row,
      sortable: true,
      cell: (row: any) => (
        <Typography
          color={checkForChildren(row) ? 'primary' : 'dark'}
          fontSize={{ xs: '0.8rem', sm: '0.9rem' }}
          fontFamily={'Atlas-Grotesk-Web-Regular'}
          sx={{
            transition: 'color 0.3s',
            textDecoration: checkForChildren(row) ? 'underline' : 'none',
            '&:hover': {
              color: checkForChildren(row) ? 'primary.dark' : '#000',
              cursor: checkForChildren(row) ? 'pointer' : 'auto',
            },
          }}
          onClick={() =>
            checkForChildren(row) ? handleClickRow(typeof row === 'string' ? row : row.id) : null
          }
        >
          {typeof row === 'string' ? row : row.id}
        </Typography>
      ),
    },
  ]

  const CustomPaginationComponent = ({
    rowCount,
    currentPage,
    rowsPerPage,
    onChangePage,
    onChangeRowsPerPage,
  }: {
    rowCount: number
    currentPage: number
    rowsPerPage: number
    onChangePage: any
    onChangeRowsPerPage: any
  }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage)

    const handleFirstPage = () => {
      if (currentPage !== 1) {
        onChangePage(1)
      }
    }

    const handlePreviousPage = () => {
      if (currentPage > 1) {
        onChangePage(currentPage - 1)
      }
    }

    const handleNextPage = () => {
      if (currentPage < totalPages) {
        onChangePage(currentPage + 1)
      }
    }

    const handleLastPage = () => {
      if (currentPage !== totalPages) {
        onChangePage(totalPages)
      }
    }
    const disabled = filteredData.length === 0

    // const handleRowsPerPageChange = (e) => {
    //   const newRowsPerPage = Number(e.target.value);
    //   setRowsPerPage(newRowsPerPage);
    //   onChangeRowsPerPage(newRowsPerPage);
    // };

    return (
      <nav
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '15px',
          background: 'white',
          borderTop: '1px solid #ddd',
          fontSize: '13px',
          fontFamily: 'Open Sans, sans-serif',
          color: 'rgba(0, 0, 0, 0.54)',
        }}
      >
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
          }}
        >
          Rows per page:
        </Box>
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            border: '1px solid #ddd',
            marginLeft: '10px',
            alignItems: 'center',
          }}
        >
          <select
            value={rowsPerPage}
            onChange={(e) => onChangeRowsPerPage(Number(e.target.value))}
            style={{
              border: 'none',
              appearance: 'none',
              paddingLeft: '8px',
            }}
          >
            {[10, 25, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path d="M7 10l5 5 5-5z"></path>
          </svg>
        </Box>
        <Box
          sx={{
            marginLeft: { xs: 'inherit', md: 'auto' },
            paddingRight: '6px',
          }}
        >
          Showing
        </Box>
        <Box style={{ paddingRight: '15px' }}>
          {`${(currentPage - 1) * rowsPerPage + 1}-${Math.min(currentPage * rowsPerPage, rowCount)} of ${rowCount} rows`}
        </Box>
        <Box
          sx={{
            marginLeft: { xs: 'auto', md: 'inherit' },
          }}
        >
          <Button
            id="pagination-first-page"
            data-testid="pagination-first-page"
            onClick={handleFirstPage}
            disabled={currentPage === 1}
            sx={{
              marginLeft: { xs: 'auto', md: 'inherit' },
              background: 'transparent',
              border: '0',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              minWidth: 'auto',
            }}
          >
            <svg
              fill={currentPage === 1 ? '#ddd' : '#000'}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z"></path>
              <path fill="none" d="M24 24H0V0h24v24z"></path>
            </svg>
          </Button>
          <Button
            id="pagination-previous-page"
            data-testid="pagination-previous-page"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            style={{
              background: 'transparent',
              border: '0',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              minWidth: 'auto',
            }}
          >
            <svg
              fill={currentPage === 1 ? '#ddd' : '#000'}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"></path>
              <path d="M0 0h24v24H0z" fill="none"></path>
            </svg>
          </Button>
          <Button
            id="pagination-next-page"
            data-testid="pagination-next-page"
            onClick={handleNextPage}
            disabled={currentPage === totalPages || disabled}
            style={{
              background: 'transparent',
              border: '0',
              cursor: currentPage === totalPages || disabled ? 'not-allowed' : 'pointer',
              minWidth: 'auto',
            }}
          >
            <svg
              fill={currentPage === totalPages || disabled ? '#ddd' : '#000'}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path>
              <path d="M0 0h24v24H0z" fill="none"></path>
            </svg>
          </Button>
          <Button
            id="pagination-last-page"
            data-testid="pagination-last-page"
            onClick={handleLastPage}
            disabled={currentPage === totalPages || disabled}
            style={{
              background: 'transparent',
              border: '0',
              cursor: currentPage === totalPages || disabled ? 'not-allowed' : 'pointer',
              minWidth: 'auto',
            }}
          >
            <svg
              fill={currentPage === totalPages || disabled ? '#ddd' : '#000'}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z"></path>
              <path fill="none" d="M0 0h24v24H0V0z"></path>
            </svg>
          </Button>
        </Box>
      </nav>
    )
  }

  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isSmallScreen ? 'column' : 'row',
          // alignItems: 'center',
          justifyContent: !singleLevelSSCC && ids?.length === 1 ? 'space-between' : 'flex-end',
          alignItems: 'center',
          my: {
            xs: 2,
            sm: 3,
          },
        }}
      >
        {!singleLevelSSCC && ids?.length === 1 ? (
          <Typography
            variant="body2"
            component="span"
            fontFamily="Sanomat-MediumItalic-Web"
            color="primary.dark"
            sx={{
              textDecoration: 'none',
              fontSize: isSmallScreen ? '1.4rem' : '2.6rem',
              textTransform: 'uppercase',
              fontWeight: 500,
              // textAlign:"left"
            }}
          >
            {ids[0]}
          </Typography>
        ) : null}
        <TextField
          variant="outlined"
          size="small"
          placeholder={inFocus ? null : 'Search'}
          label={inFocus ? 'Search' : null}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          data-testid="search-container"
          InputProps={{
            sx: {
              height: '32px',
            },
            startAdornment: (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1,
                  p: 1,
                  color: '#676767',
                }}
              >
                {/* <SearchIcon /> */}
                <svg height="15" width="15" x="0px" y="0px" viewBox="0 0 512.005 512.005">
                  <g>
                    <g>
                      <path
                        d="M505.749,475.587l-145.6-145.6c28.203-34.837,45.184-79.104,45.184-127.317c0-111.744-90.923-202.667-202.667-202.667
                        S0,90.925,0,202.669s90.923,202.667,202.667,202.667c48.213,0,92.48-16.981,127.317-45.184l145.6,145.6
                        c4.16,4.16,9.621,6.251,15.083,6.251s10.923-2.091,15.083-6.251C514.091,497.411,514.091,483.928,505.749,475.587z
                        M202.667,362.669c-88.235,0-160-71.765-160-160s71.765-160,160-160s160,71.765,160,160S290.901,362.669,202.667,362.669z"
                      ></path>
                    </g>
                  </g>
                </svg>
                {/* search icon */}
              </Box>
            ),
          }}
          sx={{
            width: '250px',
            padding: 0,
            marginTop: isSmallScreen ? 2 : 0,
            bgcolor: 'white',
          }}
        />
      </Box>
      <Box border="1px solid" borderColor="divider" style={{ marginBottom: '30px' }}>
        <>
          <DataTable
            columns={columnsForReportCardDetails}
            data={paginatedData}
            pagination
            paginationTotalRows={filteredData.length}
            paginationPerPage={rowsPerPage}
            paginationComponent={CustomPaginationComponent}
            paginationServer
            persistTableHead
            // paginationComponentOptions={{
            //   rowsPerPageOptions: [10, 25, 50, 100],
            // }}
            onChangePage={setCurrentPage}
            onChangeRowsPerPage={(newRowsPerPage) => {
              setRowsPerPage(newRowsPerPage)
              setCurrentPage(1)
            }}
            noDataComponent={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '80px',
                }}
              >
                {searchText ? (
                  <>
                    Searching for{' '}
                    {ids?.length === 1 && !singleLevelSSCC ? 'Parent Serial' : 'Serial'} &quot;
                    {searchText}&quot; returned 0 results.
                  </>
                ) : (
                  'no data found'
                )}
              </Box>
            }
          />
          {filteredData.length == 0 && (
            <CustomPaginationComponent
              rowCount={0}
              currentPage={1}
              rowsPerPage={10}
              onChangePage={null}
              onChangeRowsPerPage={null}
            />
          )}
        </>
      </Box>
    </>
  )
}

export default ListTable
